/**
 * Conversation Response Pair Component
 * Renders a single user-assistant message pair with all associated content
 */

import React, { useMemo } from 'react';
import { ChatMessage, ChatLocation } from '@/types/chatTypes';
import {
  ModeChip,
  TabItem,
  PeopleAlsoAsk as PeopleAlsoAskType,
  ExperienceCard,
} from '@/constants/chatScreenData';
import { spacing, strokeColors } from '@/design-system/utils';
import { ResponsePairHeader } from './ResponsePairHeader';
import { ResponsePairTabs } from './ResponsePairTabs';
import { ResponsePairContent } from './ResponsePairContent';
import { isTouristAttraction } from '@/utils/locationTypeHelpers';

export interface ConversationResponsePairProps {
  userMessage: ChatMessage;
  assistantMessage: ChatMessage;
  isLastPair: boolean;
  conversationId: string;
  modeChips: ModeChip[];
  activeMode?: string;
  onModeChange?: (modeId: string) => void;
  tabs: TabItem[];
  activeTab: string;
  onTabChange: (
    tabId: string,
    conversationId: string,
    isLastPair: boolean
  ) => void;
  intent?: string;
  mood?: string;
  locations: ChatLocation[]; // Raw locations from API
  onCardClick?: (cardId: string) => void;
  experiences: ExperienceCard[];
  onExperienceClick?: (experienceId: string) => void;
  overviewDescription: string;
  peopleAlsoAsk: PeopleAlsoAskType[];
  onPeopleAlsoAskClick?: (itemId: string, question: string) => void;
  onPlaceNameClick?: (placeName: string) => void;
  onLike?: (messageId: string) => void;
  onDislike?: (messageId: string) => void;
  onCopy?: (messageId?: string, content?: string) => void;
  onShare?: (messageId?: string, content?: string) => void;
  onDownload?: (messageId?: string, content?: string) => void;
  isLoading?: boolean;
  getFeedback: (messageId: string) => 'like' | 'dislike' | null;
  getIcon: (iconName?: string) => React.ReactNode;
  tabsSectionRef?: React.RefObject<HTMLDivElement>;
  headerRef?: React.RefObject<HTMLDivElement>;
  dividerAfterTabsRef?: React.RefObject<HTMLDivElement>;
}

export const ConversationResponsePair: React.FC<
  ConversationResponsePairProps
> = ({
  userMessage,
  assistantMessage,
  isLastPair,
  conversationId,
  modeChips,
  activeMode,
  onModeChange,
  tabs,
  activeTab,
  onTabChange,
  intent,
  mood,
  locations,
  onCardClick,
  experiences,
  onExperienceClick,
  overviewDescription,
  peopleAlsoAsk,
  onPeopleAlsoAskClick,
  onPlaceNameClick,
  onLike,
  onDislike,
  onCopy,
  onShare,
  onDownload,
  isLoading,
  getFeedback,
  getIcon,
  tabsSectionRef,
  headerRef,
  dividerAfterTabsRef,
}) => {
  // Filter and deduplicate locations
  const validLocations = useMemo(() => {
    if (!locations || locations.length === 0) {
      return [];
    }

    // Filter valid locations - show all locations that have place_id and a name
    const validLocations = locations.filter(location => {
      if (
        !location.place_id ||
        !(location.title || location.place || location.place_name)
      ) {
        return false;
      }

      return true;
    });

    // Deduplicate by place_id + place_name combination
    // This ensures locations with same place_id but different names are shown separately
    const seenIds = new Set<string>();
    return validLocations.filter(location => {
      const name =
        location.title || location.place || location.place_name || '';
      const id = location.place_id ? `${location.place_id}_${name}` : name;
      if (seenIds.has(id)) {
        return false;
      }
      seenIds.add(id);
      return true;
    });
  }, [locations]);

  // Discover tab should only show real/tourist places, not business or admin areas
  const discoverLocations = useMemo(() => {
    if (!validLocations.length) {
      return [];
    }

    return validLocations.filter(location => isTouristAttraction(location));
  }, [validLocations]);

  // Check if this is an error message (starts with "Sorry, I encountered an error")
  const isErrorMessage = useMemo(() => {
    return (
      assistantMessage.content &&
      assistantMessage.content
        .trim()
        .startsWith('Sorry, I encountered an error')
    );
  }, [assistantMessage.content]);

  // Check if there are any locations (for tab visibility)
  const hasPlacesForThisResponse = validLocations.length > 0;
  const hasExperiencesForThisResponse = experiences && experiences.length > 0;

  // Check if there are valid locations with coordinates for map tab
  const hasValidLocationsForMap = useMemo(() => {
    if (!locations || locations.length === 0) {
      return false;
    }

    // Check if any location has valid coordinates
    return locations.some(location => {
      return (
        location.latitude &&
        location.longitude &&
        !isNaN(location.latitude) &&
        !isNaN(location.longitude) &&
        location.latitude >= -90 &&
        location.latitude <= 90 &&
        location.longitude >= -180 &&
        location.longitude <= 180
      );
    });
  }, [locations]);

  // Map tab should be visible if there are experiences OR valid locations with coordinates
  const hasMapContent =
    hasExperiencesForThisResponse || hasValidLocationsForMap;

  let filteredTabsForResponse = tabs;

  // Hide all tabs if this is an error message
  if (isErrorMessage) {
    filteredTabsForResponse = [];
  } else {
    // Only filter tabs based on content if it's not an error
    if (!hasPlacesForThisResponse) {
      filteredTabsForResponse = filteredTabsForResponse.filter(
        tab => tab.id !== 'places'
      );
    }
    if (!hasExperiencesForThisResponse) {
      filteredTabsForResponse = filteredTabsForResponse.filter(
        tab => tab.id !== 'experiences'
      );
    }
    // Hide map tab if there are no experiences and no valid locations with coordinates
    if (!hasMapContent) {
      filteredTabsForResponse = filteredTabsForResponse.filter(
        tab => tab.id !== 'map'
      );
    }
  }

  // Ensure we have a valid active tab so content is never blank (e.g. when tabs are empty or current tab was filtered out)
  const effectiveActiveTab = filteredTabsForResponse.some(
    t => t.id === activeTab
  )
    ? activeTab
    : filteredTabsForResponse[0]?.id || 'discover';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[8],
      }}
    >
      {/* Prompt Section */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing[4],
        }}
      >
        {/* Header: Mode chips and user message */}
        <ResponsePairHeader
          userMessage={userMessage}
          modeChips={modeChips}
          activeMode={activeMode}
          onModeChange={onModeChange}
          intent={intent}
          mood={mood}
          getIcon={getIcon}
          headerRef={isLastPair ? headerRef : undefined}
        />

        {/* Tabs - Hide if error message */}
        {filteredTabsForResponse.length > 0 && (
          <div ref={isLastPair ? tabsSectionRef : undefined}>
            <ResponsePairTabs
              tabs={filteredTabsForResponse}
              activeTab={effectiveActiveTab}
              conversationId={conversationId}
              isLastPair={isLastPair}
              onTabChange={onTabChange}
            />
          </div>
        )}

        {/* Divider - Hide if error message (no tabs to separate) */}
        {filteredTabsForResponse.length > 0 && (
          <div
            ref={isLastPair ? dividerAfterTabsRef : undefined}
            style={{
              width: '100%',
              height: '1px',
              backgroundColor: strokeColors.base,
            }}
          />
        )}
      </div>

      {/* Response Section */}
      <ResponsePairContent
        assistantMessage={assistantMessage}
        activeTab={effectiveActiveTab}
        isLastPair={isLastPair}
        locations={discoverLocations}
        allLocations={validLocations}
        onCardClick={onCardClick}
        experiences={experiences}
        onExperienceClick={onExperienceClick}
        overviewDescription={overviewDescription}
        peopleAlsoAsk={peopleAlsoAsk}
        onPeopleAlsoAskClick={onPeopleAlsoAskClick}
        onPlaceNameClick={onPlaceNameClick}
        onLike={onLike}
        onDislike={onDislike}
        onCopy={onCopy}
        onShare={onShare}
        onDownload={onDownload}
        isLoading={isLoading}
        getFeedback={getFeedback}
      />
    </div>
  );
};
